import { API_URLS } from "@solomon-apps/config";
import type {
    YaasLpHoldingsSnapshot,
    YaasWalletHolding,
    YaasWalletResponse
} from "@solomon-apps/shared/src/types/yaas";

import { parseWalletViewResponse } from "./walletViewResponse";
import { mergeLpSnapshots, parseLpSnapshot } from "./yaasLpHoldings";

export function walletLpSnapshot(
    view: YaasWalletResponse
): YaasLpHoldingsSnapshot<Extract<YaasWalletHolding, { kind: "amm-position" }>> | undefined {
    const observation = view.observation;
    if (observation.status === "unavailable" && !observation.configVersion) return undefined;
    const holdings = view.holdings.filter(
        (row): row is Extract<YaasWalletHolding, { kind: "amm-position" }> => row.kind === "amm-position"
    );
    const snapshot = parseLpSnapshot(
        {
            walletAddress: observation.ownerAddress,
            configVersion: observation.configVersion,
            poolAddresses: observation.poolAddresses,
            holdings,
            complete: observation.lpComplete,
            incompleteReason: observation.lpComplete ? null : "owner_enumeration_unavailable",
            fetchedAt: observation.fetchedAt
        },
        observation.ownerAddress ?? ""
    );
    return { ...snapshot, holdings };
}

export async function fetchWalletView(address: string, signal?: AbortSignal): Promise<YaasWalletResponse> {
    const response = await fetch(`${API_URLS.YAAS}/api/yaas/wallet/${encodeURIComponent(address)}?view=v2`, { signal });
    if (!response.ok) throw new Error(`Wallet request failed (HTTP ${response.status})`);
    const view = parseWalletViewResponse(await response.json(), address);
    if (!view) throw new Error("Invalid wallet response");
    walletLpSnapshot(view);
    return view;
}

/** Retain positive LP evidence during partial scans without retaining stale account/settings data. */
export function mergeWalletViews(
    previous: YaasWalletResponse | undefined,
    next: YaasWalletResponse
): YaasWalletResponse {
    if (!previous || previous.walletAddress !== next.walletAddress) return next;
    const nextSnapshot = walletLpSnapshot(next);
    if (!nextSnapshot) {
        if (
            !previous.observation.configVersion ||
            (next.observation.ownerAddress && next.observation.ownerAddress !== previous.observation.ownerAddress)
        )
            return next;
        return {
            ...next,
            holdings: previous.holdings,
            observation: {
                ...previous.observation,
                status: "unavailable",
                reason: "discovery_unavailable",
                complete: false,
                lpComplete: false
            }
        };
    }
    const snapshot = mergeLpSnapshots(walletLpSnapshot(previous), nextSnapshot);
    return {
        ...next,
        holdings: [...next.holdings.filter((row) => row.kind === "token"), ...snapshot.holdings]
    };
}
